import type { Messages } from '../i18n/messages';

/**
 * Catalog module strings. Entity data (names, kinds, namespaces, owner refs,
 * descriptions, `spec.type`/`spec.lifecycle` values, tab kind labels like
 * "Components"/"APIs"/"Groups"/"Users" which mirror real entity-kind
 * vocabulary) is never keyed — only chrome around it is. Where a sentence
 * needs one of those kind words, it stays a `{noun}` interpolation, not part
 * of the message text.
 */
export const catalogMessages: Messages = {
  // RegaskCatalogPageView — page header
  'catalog.regaskCatalogPageView.header.title': {
    en: 'Regask Catalog',
    fun: 'Regask Zoo',
  },
  'catalog.regaskCatalogPageView.header.description': {
    en: 'Keep track of all main components and features here.',
    fun: 'Everything you own, all in one place. No excuses.',
  },
  'catalog.regaskCatalogPageView.header.refreshTooltip': {
    en: 'Refetch catalog entities (promoted tags), GitHub PR/CI and ArgoCD release signals now.',
    fun: 'Go fetch! Pulls fresh promoted tags, GitHub PR/CI and ArgoCD signals right now.',
  },

  // RegaskCatalogPageView — kind + ownership filters (kind option labels stay
  // raw entity-kind words; only the field chrome is keyed)
  'catalog.regaskCatalogPageView.kindsFilter.ariaLabel': {
    en: 'Kinds',
    fun: 'Pick a flavor',
  },
  'catalog.regaskCatalogPageView.ownershipFilter.ariaLabel': {
    en: 'Ownership',
    fun: 'Whose stuff?',
  },
  'catalog.regaskCatalogPageView.ownershipFilter.all': {
    en: 'All',
    fun: 'Everything',
  },
  'catalog.regaskCatalogPageView.ownershipFilter.owned': {
    en: 'Owned',
    fun: 'Mine',
  },
  'catalog.regaskCatalogPageView.ownershipFilter.starred': {
    en: 'Starred',
    fun: 'Faves',
  },

  // Type / Owner multi-select filters
  'catalog.regaskCatalogPageView.typeFilter.label': {
    en: 'Type',
    fun: 'Flavor',
  },
  'catalog.regaskCatalogPageView.typeFilter.placeholder': {
    en: 'All types',
    fun: 'Any flavor',
  },
  'catalog.regaskCatalogPageView.typeFilter.searchPlaceholder': {
    en: 'Search types…',
    fun: 'Hunt for a flavor…',
  },
  'catalog.regaskCatalogPageView.ownerFilter.label': {
    en: 'Owner',
    fun: 'Owner',
  },
  'catalog.regaskCatalogPageView.ownerFilter.placeholder': {
    en: 'All owners',
    fun: 'Anybody, everybody',
  },
  'catalog.regaskCatalogPageView.ownerFilter.searchPlaceholder': {
    en: 'Search owners…',
    fun: 'Hunt for an owner…',
  },

  // Card (table container) + search
  'catalog.regaskCatalogPageView.card.allTitle': {
    en: 'All {noun}',
    fun: 'All the {noun}',
  },
  'catalog.regaskCatalogPageView.clearFilters': {
    en: 'Clear filters',
    fun: 'Wipe the slate',
  },
  'catalog.regaskCatalogPageView.search': {
    en: 'Search',
    fun: 'Search',
  },

  // Loading / error states
  'catalog.regaskCatalogPageView.loading': {
    en: 'Loading the catalog…',
    fun: 'Rounding up the catalog…',
  },
  'catalog.regaskCatalogPageView.error': {
    en: 'Could not load the catalog — try again later.',
    fun: 'The catalog dodged us — try again later.',
  },

  // Empty states ({noun} is one of the raw kind nouns: components/APIs/
  // groups/users; the component table always interpolates "components")
  'catalog.regaskCatalogPageView.emptyState.title': {
    en: 'No {noun}',
    fun: 'No {noun} in sight',
  },
  'catalog.regaskCatalogPageView.emptyState.description': {
    en: 'No {noun} match the current filters.',
    fun: 'No {noun} match your filters — try loosening them up.',
  },
  'catalog.regaskCatalogPageView.simpleTable.emptyText': {
    en: 'No {noun}.',
    fun: 'No {noun} around here.',
  },

  // ComponentTable columns
  'catalog.regaskCatalogPageView.column.name': { en: 'Name', fun: 'Name' },
  'catalog.regaskCatalogPageView.column.owner': { en: 'Owner', fun: 'Owner' },
  'catalog.regaskCatalogPageView.column.type': { en: 'Type', fun: 'Flavor' },
  'catalog.regaskCatalogPageView.column.lifecycle': {
    en: 'Lifecycle',
    fun: 'Life stage',
  },
  'catalog.regaskCatalogPageView.column.description': {
    en: 'Description',
    fun: 'The lowdown',
  },
  'catalog.regaskCatalogPageView.column.releases': {
    en: 'Releases',
    fun: 'Drops',
  },
  'catalog.regaskCatalogPageView.column.ci': { en: 'CI', fun: 'CI' },
  'catalog.regaskCatalogPageView.column.prs': { en: 'PRs', fun: 'PRs' },

  // Releases column filter popover
  'catalog.regaskCatalogPageView.releasesFilter.tagAriaLabel': {
    en: 'Tag contains',
    fun: 'Tag contains',
  },
  'catalog.regaskCatalogPageView.releasesFilter.tagPlaceholder': {
    en: 'Tag contains…',
    fun: 'Tag looks like…',
  },
  'catalog.regaskCatalogPageView.releasesFilter.deployedIn': {
    en: 'Deployed in',
    fun: 'Living in',
  },

  // CI column filter popover
  'catalog.regaskCatalogPageView.ciFilter.ariaLabel': {
    en: 'CI status',
    fun: 'CI vibes',
  },
  'catalog.regaskCatalogPageView.ciFilter.all': { en: 'All', fun: 'All' },
  'catalog.regaskCatalogPageView.ciFilter.passing': {
    en: 'Passing',
    fun: 'Passing',
  },
  'catalog.regaskCatalogPageView.ciFilter.failing': {
    en: 'Failing',
    fun: 'Failing',
  },
  'catalog.regaskCatalogPageView.ciBadge.failed': {
    en: '{count} failed',
    fun: '{count} whoopsies',
  },
  'catalog.regaskCatalogPageView.ciBadge.passing': {
    en: 'passing',
    fun: 'smooth sailing',
  },

  // PRs column filter popover
  'catalog.regaskCatalogPageView.prsFilter.onlyOpen': {
    en: 'Only with open PRs',
    fun: 'Only the ones with open PRs',
  },

  // Row actions
  'catalog.regaskCatalogPageView.rowActions.view': { en: 'View', fun: 'Peek' },
  'catalog.regaskCatalogPageView.rowActions.star': { en: 'Star', fun: 'Star' },
  'catalog.regaskCatalogPageView.rowActions.unstar': {
    en: 'Unstar',
    fun: 'Unstar',
  },

  // EntityPageHeaderView
  'catalog.entityPageHeaderView.viewSource': {
    en: 'View Source',
    fun: 'Peek at the Source',
  },
  'catalog.entityPageHeaderView.owner': { en: 'Owner', fun: 'Owner' },
  'catalog.entityPageHeaderView.owners': { en: 'Owners', fun: 'Owners' },
  'catalog.entityPageHeaderView.lifecycle': {
    en: 'Lifecycle',
    fun: 'Life stage',
  },
  'catalog.entityPageHeaderView.tags': { en: 'Tags', fun: 'Tags' },

  // Entity-content tab titles (module-scope config, resolved once at
  // registration — see ArgocdEntityContent.tsx / DeploymentsEntityContent.tsx)
  'catalog.argocdEntityContent.title': { en: 'ArgoCD', fun: 'ArgoCD' },
  'catalog.deploymentsEntityContent.title': {
    en: 'Deployments',
    fun: 'Ship Log',
  },
};
